import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@opendoor/bricks-next';
import { buildCarouselPhotos } from 'components/marketplace/helpers';
import { removeAddressPreviewParams } from 'components/marketplace/helpers/useSearchQueries';
import { PhotoCarousel as MapPhotoCarousel } from 'components/marketplace/map/PhotoCarousel';
import { FooterCTA, Lightbox, ShareModal } from 'components/marketplace/property';
import type { PropertyDetailsType } from 'components/marketplace/property';
import { PropertyCTAs } from 'components/marketplace/property/PropertySection/PropertyCTAs';
import { PropertyDetails } from 'components/marketplace/property/PropertySection/PropertyDetails';
import { PropertyStatus } from 'components/marketplace/property/PropertySection/PropertyStatus';
import { PropertySummary } from 'components/marketplace/property/PropertySection/PropertySummary';
import { Divider } from 'components/shared/Divider';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';

interface MiniPropertyDetailPageProps {
  property: PropertyDetailsType;
  onClose: () => void;
}

export const MiniPropertyDetailPage = ({ property, onClose }: MiniPropertyDetailPageProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const ctaRef = useRef<HTMLDivElement>(null);
  const { inView } = useInViewAfterLoad({ ref: ctaRef, threshold: 0, initialInView: true });

  // Refs mirror the overlay open flags synchronously so our keydown handler
  // always reads the latest value without depending on React state batching.
  const isLightboxOpenRef = useRef(false);
  const isShareModalOpenRef = useRef(false);

  const handleOnClose = useCallback(() => {
    const urlParams = removeAddressPreviewParams();
    // `replaceState` swaps the current history entry in place; it does NOT
    // push a new one, so repeated Escape-close → reopen cycles cannot stack
    // entries and break the browser Back button.
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
    onClose();
  }, [onClose]);

  // Close the mini PDP on Escape. Register in the capture phase so this
  // handler always runs before the bricks-next Lightbox's document-level
  // bubble-phase keydown listener. That guarantees a stable Escape precedence
  // (Lightbox > ShareModal > mini PDP) regardless of mount order: if the
  // Lightbox is open when Escape is pressed, the ref is still true here, we
  // bail, and the Lightbox's own listener runs next and closes it. Without
  // capture-phase ordering, a flipped mount sequence (StrictMode remount or
  // future code-splitting reshuffle) could let the Lightbox close first,
  // flip the ref to false, and then the mini PDP handler would incorrectly
  // also close the panel on the same keypress.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      if (isLightboxOpenRef.current || isShareModalOpenRef.current) {
        return;
      }
      handleOnClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleOnClose]);

  // Floor plans intentionally omitted here: the bricks-next PhotoCarousel
  // cover-crops portrait plans. They remain accessible via the Lightbox,
  // which renders the full ordered list (listing photos + floor plans) with
  // resizeMode: contain.
  //
  // Kept above the `!property` early return so all hooks run in the same
  // order on every render (react-hooks/rules-of-hooks).
  const carouselPhotos = useMemo(
    () => buildCarouselPhotos(property),
    [property?.photos, property?.floorPlanPhotos],
  );

  if (!property) {
    return <Box />;
  }

  const handleToggleShareModal = () => {
    setIsShareModalOpen((prev) => {
      const next = !prev;
      isShareModalOpenRef.current = next;
      return next;
    });
  };

  // Separate open/close handlers instead of a toggle: bricks-next `Lightbox`
  // registers a document-level keydown listener that fires `handleClose()` on
  // Escape regardless of `isOpen`. Passing a toggle would flip a closed
  // lightbox OPEN on Escape while the mini PDP is active.
  const openLightbox = () => {
    isLightboxOpenRef.current = true;
    setSelectedPhotoIndex(0);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    isLightboxOpenRef.current = false;
    setIsLightboxOpen(false);
  };

  return (
    <Box height="100%" key={`mini-pdp-${property?.addressToken}`}>
      <MapPhotoCarousel
        photos={carouselPhotos}
        onClose={handleOnClose}
        onOpenShareModal={handleToggleShareModal}
        onPhotoClick={openLightbox}
        key={`mini-pdp-carousel-${property?.addressToken}`}
      />
      <Box position="relative">
        <Box px={48} pt={24}>
          <PropertyStatus property={property} />
          <PropertySummary property={property} />
          <Box ref={ctaRef}>
            <PropertyCTAs property={property} />
          </Box>
          <Divider className="pt-12" />
          <PropertyDetails property={property} />
        </Box>
        <FooterCTA property={property} isVisible={!inView} />
        {isLightboxOpen && (
          <Lightbox
            handleClose={closeLightbox}
            isOpen={isLightboxOpen}
            property={property}
            selectedPhotoIndex={selectedPhotoIndex}
          />
        )}
      </Box>
      <ShareModal
        key={`share-modal-${property?.addressToken}`}
        property={property}
        isOpen={isShareModalOpen}
        onClose={handleToggleShareModal}
      />
    </Box>
  );
};
